import { log } from '@domgen/shared/src/utils/log';
import { AppRoute } from '../../enums/appRoutes';
import { StepKey } from '../../enums/stepKey';
import { getRoutes } from './getRoutes';

/**
 * @description This function takes the current step key and journey type as arguments and returns the path of the next step in the journey.
 *
 * It first retrieves the routes for the given journey type, filters out any routes that do not have a stepKey,
 * and then finds the index of the current step key in the filtered routes.
 * If the current step key is not found or is the last step in the routes, it returns null. Otherwise, it returns the path of the next step.
 *
 * This is the core place of the logic for determining navigation between steps in the application,
 * and it relies on the structure of the routes defined for each journey type.
 *
 * [!IMPORTANT]
 * This function assumes that the routes are defined in the correct order of the steps in the journey, and that each route has a unique stepKey.
 * Only use within the scope of the store event handlers to determine the next step path for navigation.
 *
 * @param {StepKey} stepKey - The key of the current step in the journey.
 * @param {'control' | 'test'} journeyType - The type of the journey, which determines which set of routes to use.
 *
 * @return {AppRoute | null} The path of the next step in the journey, or null if there is no next step.
 * @summary This function determines the next step in the journey based on the current step key and journey type.

 */
export const getNextStepPath = (
  stepKey: StepKey,
  journeyType: 'control' | 'test',
): AppRoute | null => {
  const { formStepRoutes } = getRoutes(journeyType);
  const currentRouteIndex = formStepRoutes.findIndex((route) => route.stepKey === stepKey);
  log.debug({
    message: 'Finding next step path',
    data: {
      stepKey,
      journeyType,
      currentRouteIndex,
      routes: formStepRoutes.map((r) => ({ path: r.path, stepKey: r.stepKey })),
    },
  });
  if (currentRouteIndex === -1 || currentRouteIndex === formStepRoutes.length - 1) {
    return null;
  }
  return formStepRoutes[currentRouteIndex + 1].path;
};
